import React, { useState, useMemo } from 'react';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import {
  Button,
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Modal,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@material-ui/core';

import { Employee_Salary, Employee_Expense } from 'constants/expenseTypes';

const EmployeeInfoModal = (props) => {

  const [tabValue, setTabValue] = useState(0);

  const expectedList = useMemo(() => {
    const initialExpectedList = [];

    for ( let i = 0; i < props.employeeInfo.expectedSalaries.length; i++ ) {
      const expectedSalary = props.employeeInfo.expectedSalaries[i];
      initialExpectedList.push({
        expectedSalary: expectedSalary.total,
        monthYear: moment(expectedSalary.monthYear).format("MMMM / YYYY"),
        creationDate: moment(expectedSalary.createdAt).format("DD/MM/YYYY")
      });
    }
  
    return initialExpectedList;
  
  }, [props.employeeInfo.expectedSalaries]);

  const { totalBalance, salaryList, expenseList, totalExpensesList } = useMemo(() => {

    let totalBalanceBuffer = 0;
    let salaryListBuffer = [];
    let expenseListBuffer = [];
    let totalExpensesListBuffer = [];

    let startDate = moment(props.employeeInfo.joiningDate);
    let todaysDate = moment().startOf('day');

    for ( let i = 0; i < props.expenses.length; i++ ) {
      // Condition to check if any Employee Salary is present in Expenses.
      if(props.employeeInfo.id === props.expenses[i].employeeId) {
        if(props.expenses[i].type === Employee_Salary) {
          const salary = props.expenses[i];
          salaryListBuffer.push({
            displayId: salary.displayId,
            monthYear: moment(salary.monthYear).format("MMMM / YYYY"),
            amount: salary.total,
            description: salary.description,
            momentDate: salary.monthYear,
            txnDate: moment(salary.createdAt).format("DD/MM/YYYY")
          });
        }
        else if(props.expenses[i].type === Employee_Expense) {
          const expense = props.expenses[i];
          expenseListBuffer.push({
            displayId: expense.displayId,
            amount: expense.total,
            description: expense.description,
            momentDate: expense.createdAt,
            txnDate: moment(expense.createdAt).format("DD/MM/YYYY")
          });
        }
      }
    }

    // The additional 'OR' condition is important to make sure the loop runs even if the months are the same.
    while (startDate.isBefore(todaysDate) || startDate.isSame(todaysDate, 'month')) {

      let totalMonthExpenses = expenseListBuffer.reduce((prevExpense, currentExpense) => {
        if(startDate.isSame(currentExpense.momentDate, 'month')) return prevExpense + currentExpense.amount;
        else return prevExpense;
      }, 0);
      let totalMonthSalary = salaryListBuffer.find(salary => startDate.isSame(salary.momentDate, 'month'))?.amount || 0;
      let totalMonthExpectedSalary = props.employeeInfo.expectedSalaries.find(expectedSalary => startDate.isSame(expectedSalary.monthYear, 'month'))?.total || 0;

      totalBalanceBuffer += totalMonthExpectedSalary - ( totalMonthExpenses + totalMonthSalary );
      totalExpensesListBuffer.push({
        monthYear: startDate.format("MMMM / YYYY"),
        expectedSalary: totalMonthExpectedSalary,
        totalExpenses: totalMonthExpenses + totalMonthSalary,
        balance: totalMonthExpectedSalary - ( totalMonthExpenses + totalMonthSalary )
      });

      startDate.add(1, 'month');
    }

    return {
      totalBalance: totalBalanceBuffer,
      salaryList: salaryListBuffer,
      expenseList: expenseListBuffer,
      totalExpensesList: totalExpensesListBuffer
    }
  
  }, [props.expenses, props.employeeInfo]);

  const columns = [
    { label: "Expense ID", name: "displayId" },
    { label: "Month & Year", name: "monthYear" },
    { label: "Amount", name: "amount" },
    { label: "Description", name: "description" },
    { label: "Expected Salary", name: "expectedSalary" },
    { label: "Transaction Date", name: "txnDate" },
    { label: "Creation Date", name: "creationDate" },
    { label: "Total Expenses", name: "totalExpenses" },
    {
      label: "Balance Amount",
      name: "balance",
      options: {
        filter: true,
        sort: true,
        viewColumns: false,
        customBodyRender: (value, tableMeta) => (
          <font color={ value < 0 ? "red" : null }>{value}</font>
        )
      }
    }
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const options = {
    selectableRows: 'none',
    viewColumns: false
  };

  return (
    <Modal open={props.open}>
      <Card>
        <CardHeader
          subheader="Review all the Expenses of the Employee"
          title="Employee History"
        />
        <Divider />
        <CardContent>
          <Box>
            <Box
              sx={{
                display: 'flex'
              }}
            >
              <Paper>
                <Tabs
                  value={tabValue}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={handleTabChange}
                  centered
                >
                  <Tab label="Salary History"/>
                  <Tab label="Employee Expenses"/>
                  <Tab label="Expected Salary"/>
                  <Tab label="Total Expenses"/>
                </Tabs>
              </Paper>
            </Box>
            { tabValue === 0 ?
              <Box
                sx={{
                  overflow: 'auto',
                  height: 458
                }}
              >
                <MUIDataTable
                  title={"Salary History"}
                  data={salaryList}
                  columns={columns.filter(column => !["expectedSalary", "creationDate", "totalExpenses", "balance"].includes(column.name))}
                  options={options}
                />
              </Box>
            : null }
            { tabValue === 1 ?
              <Box
                sx={{
                  overflow: 'auto',
                  height: 458
                }}
              >
                <MUIDataTable
                  title={"Employee Expenses"}
                  data={expenseList}
                  columns={columns.filter(column => ["displayId", "amount", "description", "txnDate"].includes(column.name))}
                  options={options}
                />
              </Box>
            : null }
            { tabValue === 2 ?
              <Box
                sx={{
                  overflow: 'auto',
                  height: 458
                }}
              >
                <MUIDataTable
                  title={"Expected Salary"}
                  data={expectedList}
                  columns={columns.filter(column => ["monthYear", "expectedSalary", "creationDate"].includes(column.name))}
                  options={options}
                />
              </Box>
            : null }
            { tabValue === 3 ?
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '1%'
                  }}
                >
                  <Typography
                    align="center"
                    color="textPrimary"
                    variant="h5"
                  >
                    Total Balance Amount : <font color={ totalBalance < 0 ? "red" : null }>{totalBalance} AED</font>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    overflow: 'auto',
                    height: 407
                  }}
                >
                  <MUIDataTable
                    title={"Total Expenses"}
                    data={totalExpensesList}
                    columns={columns.filter(column => ["monthYear", "expectedSalary", "totalExpenses", "balance"].includes(column.name))}
                    options={options}
                  />
                </Box>
              </Box>
            : null }
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1%'
              }}
            >
              <Button
                color="primary"
                variant="contained"
                onClick={props.closeModal}
              >
                Close
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  )
};

export default EmployeeInfoModal;